import React, { useMemo, useState } from 'react';
import { createAttempt, submitReading } from '../../api/exams';
import { setPartProgress, computeMistakes, scoreReadingPart } from './_progress';
import { T } from './_theme';
import {
  ReadingFrame, PartTopBar, FeedbackCard, PrimaryBtn, GhostBtn, NudgeBanner,
  readArabicPref, readingHomeHref,
} from './_primitives';

// FET Reading — Part 4 · People ↔ Place
//
// Content shape:
//   { items: [{
//       n,
//       place: { name, body },
//       people: [{ id, name, need }, ...],
//       correct: 'p2',
//       why_en, why_ar, mistake_en, mistake_ar, evidence,
//       examples: [{good|bad, text}]
//   }, ...] }
// Submission key per item: q_{i} → person id

export default function Part4Screen({
  isMobile, navigate, exam, loading,
  onContinueToNextPart, nextPartLabel,
}) {
  const part = useMemo(
    () => (exam?.reading_parts || []).find((p) => p.part_number === 4),
    [exam],
  );
  const items = useMemo(() => (part?.content?.items || []).filter(Boolean), [part]);
  const total = items.length;

  const [arabicOn, setArabicOn] = useState(readArabicPref);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [finalResult, setFinalResult] = useState(null);
  const [error, setError] = useState('');

  const persistArabic = (next) => {
    setArabicOn(next);
    try { window.localStorage.setItem('fet_reading_arabic', next ? '1' : '0'); } catch {}
  };

  if (loading || !exam) {
    return <ReadingFrame isMobile={isMobile}><div style={{ padding: 32, color: T.ink2, fontFamily: T.sans }}>Loading reading exam…</div></ReadingFrame>;
  }
  if (!part || !total) {
    return (
      <ReadingFrame isMobile={isMobile}>
        <PartTopBar partNumber={4} partTitle="People ↔ Place" currentIdx={0} total={1} onBack={() => navigate(readingHomeHref())} />
        <div style={{ padding: 24, color: T.ink2, fontFamily: T.sans, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 700, marginBottom: 6, color: T.ink }}>Part 4 isn't ready</div>
          <div>An admin needs to add at least one place + people grouping.</div>
        </div>
      </ReadingFrame>
    );
  }

  if (finalResult) {
    const pct = finalResult.max ? Math.round((finalResult.score / finalResult.max) * 100) : 0;
    return (
      <ReadingFrame isMobile={isMobile}>
        <PartTopBar partNumber={4} partTitle="People ↔ Place" currentIdx={total - 1} total={total} onBack={() => navigate(readingHomeHref())} />
        <div style={{ padding: 24 }}>
          <div style={{ background: T.surface, border: `1px solid ${T.hairline}`, borderRadius: 18, padding: 22, textAlign: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: T.ink3, marginBottom: 12 }}>Part 4 result</div>
            <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 48, fontWeight: 700, color: T.blue, fontVariantNumeric: 'tabular-nums' }}>{finalResult.score}</span>
              <span style={{ fontSize: 20, color: T.ink3, fontWeight: 600 }}>/ {finalResult.max}</span>
            </div>
            <div style={{ fontSize: 14, color: T.ink2, marginTop: 8 }}>You scored <strong style={{ color: T.ink }}>{pct}%</strong>.</div>
            <div style={{ marginTop: 18, display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <GhostBtn onClick={() => { setAnswers({}); setCurrentIdx(0); setFinalResult(null); setError(''); }}>Try again</GhostBtn>
              {onContinueToNextPart ? (
                <PrimaryBtn full={false} onClick={onContinueToNextPart}>{nextPartLabel || 'Next part →'}</PrimaryBtn>
              ) : (
                <PrimaryBtn full={false} onClick={() => navigate('/dashboard')}>Back to dashboard</PrimaryBtn>
              )}
            </div>
          </div>
        </div>
      </ReadingFrame>
    );
  }

  const item = items[currentIdx] || {};
  const place = item.place || {};
  const people = Array.isArray(item.people) ? item.people : [];
  const correctId = String(item.correct || '').toLowerCase();
  const picked = answers[`q_${currentIdx}`] || null;
  const answered = picked !== null;
  const isCorrect = answered && picked.toLowerCase() === correctId;
  const isLast = currentIdx === total - 1;
  const correctPerson = people.find((p) => p.id === correctId);
  const pickedPerson = people.find((p) => p.id === picked);

  const pickAnswer = (id) => {
    if (answered) return;
    setAnswers((prev) => ({ ...prev, [`q_${currentIdx}`]: id }));
  };

  const goNext = async () => {
    if (!isLast) {
      setCurrentIdx((i) => Math.min(total - 1, i + 1));
      return;
    }
    setSubmitting(true);
    setError('');
    const local = scoreReadingPart(4, part?.content, answers);
    const result = {
      score: local.score,
      max: local.max || total,
      mistakes: computeMistakes(4, part?.content, answers),
    };
    setFinalResult(result);
    setPartProgress(4, result);
    try {
      const attempt = await createAttempt(exam.id, 'practice', 'reading');
      await submitReading(attempt.id, { '4': answers });
    } catch (err) {
      const apiErr = err?.response?.data;
      setError(apiErr?.error || 'Could not save your attempt to the server. Your local score is shown.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ReadingFrame isMobile={isMobile}>
      <PartTopBar partNumber={4} partTitle="People ↔ Place" currentIdx={currentIdx} total={total} onBack={() => navigate(readingHomeHref())} />
      <div style={{ flex: 1, padding: 16 }}>
        <div style={{ fontSize: 14, color: T.ink2, marginBottom: 10 }}>Who is this place right for?</div>
        <div style={{
          padding: '14px 16px', background: T.surface, borderRadius: 14,
          border: `1px solid ${T.hairline}`,
          borderLeft: `4px solid ${T.p4}`,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.p4, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 4, fontFamily: T.sans }}>Place</div>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6, color: T.ink, fontFamily: T.sans }}>{place.name}</div>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: T.ink2, fontFamily: T.serif }}>{place.body}</p>
        </div>

        <div style={{ marginTop: 16, marginBottom: 8, fontSize: 12, color: T.ink3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>Which person fits?</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Each person's needs are always visible, like in the full
              exam — students compare all six at once instead of
              tapping through an accordion. The "Pick {name}" button is
              the only commit action; once any person is picked the
              other rows dim and the picked one keeps its result
              styling (green for correct, red for wrong). */}
          {people.map((p) => {
            const isPicked = picked === p.id;
            const isThisCorrect = answered && p.id === correctId;
            const isThisWrongPick = answered && isPicked && !isThisCorrect;
            const bg = isThisCorrect ? T.okBg : isThisWrongPick ? T.missBg : T.surface;
            const bd = isThisCorrect ? T.ok : isThisWrongPick ? T.miss : (isPicked ? T.blue : T.hairline);
            return (
              <div key={p.id} style={{
                background: bg,
                borderRadius: 14,
                border: `1.5px solid ${bd}`,
                padding: '12px 14px',
                opacity: answered && !isThisCorrect && !isPicked ? 0.6 : 1,
                fontFamily: T.sans,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{
                    flexShrink: 0, width: 26, height: 26, borderRadius: 13,
                    background: isThisCorrect ? T.ok : isThisWrongPick ? T.miss : T.bgSunk,
                    color: isThisCorrect || isThisWrongPick ? '#fff' : T.ink2,
                    display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700,
                  }}>{isThisCorrect ? '✓' : isThisWrongPick ? '✕' : (p.name || '·')[0]}</div>
                  <div style={{ flex: 1, fontSize: 14, fontWeight: 700, color: T.ink }}>{p.name}</div>
                </div>
                <div style={{ fontSize: 13, color: T.ink2, lineHeight: 1.55, marginLeft: 36, marginBottom: !answered ? 10 : 0 }}>{p.need}</div>
                {!answered && (
                  <div style={{ marginLeft: 36 }}>
                    <button
                      type="button"
                      onClick={() => pickAnswer(p.id)}
                      style={{
                        padding: '7px 14px',
                        borderRadius: 999,
                        border: `1px solid ${T.blue}`,
                        background: T.blue,
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: 'pointer',
                        fontFamily: T.sans,
                      }}
                    >
                      Pick {p.name}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {answered && (
          <FeedbackCard
            correct={isCorrect}
            header={isCorrect ? `Correct — it's ${correctPerson?.name || 'right'}` : `Almost — it's ${correctPerson?.name || 'someone else'}`}
            picked={pickedPerson?.name || picked}
            lang={arabicOn ? 'ar' : 'en'}
            onLangChange={(v) => persistArabic(v === 'ar')}
            simple={arabicOn ? item.why_ar : item.why_en}
            mistake={!isCorrect ? (arabicOn ? item.mistake_ar : item.mistake_en) : null}
            examples={Array.isArray(item.examples) ? item.examples : null}
            evidence={item.evidence}
          />
        )}

        {error && <div style={{ marginTop: 12, fontSize: 13, color: T.miss }}>{error}</div>}
        {!answered && <div style={{ marginTop: 14 }}><NudgeBanner>Compare each person's needs with the place, then pick the one that fits best.</NudgeBanner></div>}
      </div>

      <div style={{ padding: '12px 16px', borderTop: `1px solid ${T.hairline}`, background: T.surface, display: 'flex', gap: 10 }}>
        <GhostBtn disabled={currentIdx === 0} onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}>← Previous</GhostBtn>
        <div style={{ flex: 1 }} />
        <PrimaryBtn full={false} disabled={!answered || submitting} onClick={goNext}>
          {submitting ? 'Saving…' : isLast ? 'Finish part →' : 'Next question →'}
        </PrimaryBtn>
      </div>
    </ReadingFrame>
  );
}
